import { read as readWorkbook, utils as xlsxUtils } from "xlsx";

export enum NameFormats {
    FIRST_NAME_LAST_NAME = "FIRST_NAME_LAST_NAME",
    LAST_NAME_FIRST_NAME = "LAST_NAME_FIRST_NAME",
    FIRST_NAME = "FIRST_NAME",
    LAST_NAME = "LAST_NAME",
    FIRST_NAME_LAST_INITIAL = "FIRST_NAME_LAST_INITIAL",
    LAST_NAME_FIRST_INITIAL = "LAST_NAME_FIRST_INITIAL",
    LAST_INITIAL_FIRST_INITIAL = "LAST_INITIAL_FIRST_INITIAL",
    FIRST_INITIAL_LAST_INITIAL = "FIRST_INITIAL_LAST_INITIAL",
}

export interface DetectedStudentColumns {
    hasHeader: boolean;
    firstNameColumnIndex: number;
    lastNameColumnIndex: number | null;
}

const _toInitial = (name: string): string => (name ? `${name.charAt(0)}.` : "");

const _findHeaderIndex = (headerRow: string[], title: string): number =>
    headerRow.findIndex((cell) => cell.trim().toLowerCase() === title);

// Header combinations from the ticket, in priority order. "name" counts as the first name when
// paired with "nachname", but as the last name when paired with "vorname".
const HEADER_COMBINATIONS: Array<{ firstNameTitle: string; lastNameTitle: string }> = [
    { firstNameTitle: "vorname", lastNameTitle: "nachname" },
    { firstNameTitle: "name", lastNameTitle: "nachname" },
    { firstNameTitle: "vorname", lastNameTitle: "name" },
];

export const detectStudentColumns = (rows: string[][]): DetectedStudentColumns => {
    const headerRow = rows[0] ?? [];

    for (const combination of HEADER_COMBINATIONS) {
        const firstNameColumnIndex = _findHeaderIndex(headerRow, combination.firstNameTitle);
        const lastNameColumnIndex = _findHeaderIndex(headerRow, combination.lastNameTitle);
        if (firstNameColumnIndex >= 0 && lastNameColumnIndex >= 0) {
            return { hasHeader: true, firstNameColumnIndex, lastNameColumnIndex };
        }
    }

    const singleNameColumnIndex = _findHeaderIndex(headerRow, "name");
    if (singleNameColumnIndex >= 0) {
        return { hasHeader: true, firstNameColumnIndex: singleNameColumnIndex, lastNameColumnIndex: null };
    }

    return { hasHeader: false, firstNameColumnIndex: 0, lastNameColumnIndex: null };
};

export interface ExtractStudentNamesConfig {
    hasHeader: boolean;
    firstNameColumnIndex: number;
    lastNameColumnIndex: number | null;
    format: NameFormats;
}

export interface SplitStudentNamesResult {
    uniqueNames: string[];
    skippedExistingNames: string[];
    skippedDuplicateNames: string[];
}

const _isBinaryExcelFile = (bytes: Uint8Array): boolean => {
    const isZip = bytes[0] === 0x50 && bytes[1] === 0x4b; // "PK" → .xlsx
    const isOle = bytes[0] === 0xd0 && bytes[1] === 0xcf && bytes[2] === 0x11 && bytes[3] === 0xe0; // legacy .xls
    return isZip || isOle;
};

// Control characters (except tabs and line breaks) never appear in real CSV exports, so they mark a
// binary file (e.g. an image renamed to .csv) that would otherwise decode to mojibake via windows-1252.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTERS_REGEX = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f]/;

const _decodeText = (data: ArrayBuffer): string => {
    let text: string;
    try {
        text = new TextDecoder("utf-8", { fatal: true }).decode(data);
    } catch {
        // Excel on Windows commonly exports CSV as windows-1252 rather than UTF-8.
        text = new TextDecoder("windows-1252").decode(data);
    }

    if (CONTROL_CHARACTERS_REGEX.test(text)) {
        throw new Error("File is not a readable text table");
    }

    return text;
};

const _detectDelimiter = (text: string): string => {
    const sample = text.split(/\r?\n/).slice(0, 10).join("\n");
    const candidates = [";", ",", "\t"];
    const counts = candidates.map((delimiter) => sample.split(delimiter).length - 1);
    const maxCount = Math.max(...counts);
    return maxCount > 0 ? candidates[counts.indexOf(maxCount)] : ",";
};

export const parseStudentTable = (data: ArrayBuffer): string[][] => {
    const bytes = new Uint8Array(data);

    const workbook = _isBinaryExcelFile(bytes)
        ? readWorkbook(data, { type: "array" })
        : (() => {
              const text = _decodeText(data);
              return readWorkbook(text, { type: "string", FS: _detectDelimiter(text), raw: true });
          })();

    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
    if (!firstSheet) {
        throw new Error("File contains no sheets");
    }

    const rawRows: unknown[][] = xlsxUtils.sheet_to_json(firstSheet, { header: 1, defval: "", raw: false });
    const rows = rawRows
        .map((row) => {
            const cells = row.map((cell) => String(cell ?? "").trim());
            while (cells.length > 0 && cells[cells.length - 1] === "") {
                cells.pop();
            }
            return cells;
        })
        .filter((row) => row.length > 0);

    if (rows.length === 0) {
        throw new Error("File contains no usable rows");
    }

    return rows;
};

export const getColumnLetter = (columnIndex: number): string => {
    let letter = "";
    let remaining = columnIndex;
    while (remaining >= 0) {
        letter = String.fromCharCode(65 + (remaining % 26)) + letter;
        remaining = Math.floor(remaining / 26) - 1;
    }
    return letter;
};

export const extractStudentNames = (rows: string[][], config: ExtractStudentNamesConfig): string[] => {
    const dataRows = config.hasHeader ? rows.slice(1) : rows;

    return dataRows
        .map((row) => {
            const firstName = row[config.firstNameColumnIndex] ?? "";
            const lastName = config.lastNameColumnIndex === null ? "" : row[config.lastNameColumnIndex] ?? "";
            return formatStudentName(config.format, firstName, lastName);
        })
        .filter((name) => name.length > 0);
};

export const splitStudentNames = (names: string[], existingNames: string[]): SplitStudentNamesResult => {
    const existingNamesLower = existingNames.map((name) => name.toLowerCase());
    const seenLower = new Set<string>();
    const result: SplitStudentNamesResult = { uniqueNames: [], skippedExistingNames: [], skippedDuplicateNames: [] };

    names.forEach((name) => {
        const nameLower = name.toLowerCase();
        if (existingNamesLower.includes(nameLower)) {
            result.skippedExistingNames.push(name);
        } else if (seenLower.has(nameLower)) {
            result.skippedDuplicateNames.push(name);
        } else {
            seenLower.add(nameLower);
            result.uniqueNames.push(name);
        }
    });

    return result;
};

export const formatStudentName = (format: NameFormats, firstName: string, lastName: string): string => {
    const first = firstName.trim();
    const last = lastName.trim();

    const parts: Record<NameFormats, string[]> = {
        [NameFormats.FIRST_NAME_LAST_NAME]: [first, last],
        [NameFormats.LAST_NAME_FIRST_NAME]: [last, first],
        [NameFormats.FIRST_NAME]: [first],
        [NameFormats.LAST_NAME]: [last],
        [NameFormats.FIRST_NAME_LAST_INITIAL]: [first, _toInitial(last)],
        [NameFormats.LAST_NAME_FIRST_INITIAL]: [last, _toInitial(first)],
        [NameFormats.LAST_INITIAL_FIRST_INITIAL]: [_toInitial(last), _toInitial(first)],
        [NameFormats.FIRST_INITIAL_LAST_INITIAL]: [_toInitial(first), _toInitial(last)],
    };

    return parts[format].filter(Boolean).join(" ");
};
